// Pairing a phone, and the roster that appears once there is more than one.
//
// Enrolment is the gate on transfer (§20.9). A phone can be handed a job
// only if this console already holds its public key, which costs a real
// minute in a depot and buys the property the whole channel rests on:
// "whoever holds these bytes" becomes "one phone I named".
//
// The fingerprint is eight hex characters and is **not** a cryptographic
// binding. It is what two people can read to each other in three seconds
// in a doorway, which is the comparison that actually happens. The screen
// says so rather than dressing it up.

import { useCallback, useEffect, useRef, useState } from "react";
import { Copyable, Note, Panel, Qr } from "./Bits";
import { toHex, type DispatchBundle } from "../ops/engine";
import type { PairingCandidate, PairingOffer, ThreadChannel } from "@wayfind/core";
import { localId, type FleetState, type StoredDevice } from "../ops/state";
import { dateWords, type Vocabulary } from "../ops/words";
import { config, nowSeconds } from "../config";

export function PairPhone({
  threads,
  channel,
  state,
  vocabulary,
  onEnrol,
  onClose
}: {
  threads: DispatchBundle | null;
  /**
   * The live thread channel, when this tab has one. Mesh pairing needs a
   * peer — without it the paste box below is the whole of enrolment, and
   * that is a legitimate state rather than a broken one: a console on a
   * laptop with no mesh still enrols phones, just by hand.
   */
  channel: ThreadChannel | null;
  state: FleetState;
  vocabulary: Vocabulary;
  onEnrol(device: StoredDevice): void;
  onClose(): void;
}): JSX.Element {
  const [text, setText] = useState("");
  const [pending, setPending] = useState<{ name: string; publicKeyHex: string; fingerprint: string } | null>(null);
  const [name, setName] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  // --- Mesh pairing (§16) --------------------------------------------
  //
  // The scan flipped: this console shows a QR, the phone scans it and
  // answers over the mesh. What arrives is a *candidate* — the console
  // enrols nobody until the two fingerprints are read aloud and somebody
  // presses the button, exactly as the paste flow below has always ended.
  const [offer, setOffer] = useState<PairingOffer | null>(null);
  const [offerUrl, setOfferUrl] = useState<string>("");
  const [candidates, setCandidates] = useState<PairingCandidate[]>([]);
  const [pairingProblem, setPairingProblem] = useState<string | null>(null);
  const offerRef = useRef<PairingOffer | null>(null);

  const startPairing = useCallback(async () => {
    if (!channel || !threads) return;
    setPairingProblem(null);
    try {
      offerRef.current?.stop();
      const handle = await channel.startPairing({
        label: state.seed?.label || "Dispatch",
        addresses: state.seed?.addresses ?? []
      });
      offerRef.current = handle;
      setOffer(handle);
      setOfferUrl(threads.pairingOfferUrl(handle.offerBytes));
      setCandidates([]);
    } catch (error) {
      setPairingProblem(error instanceof Error ? error.message : String(error));
    }
  }, [channel, threads, state.seed]);

  // Poll for replies. The handle mutates in place as gossip arrives, so
  // this reads it rather than being pushed to — a second of latency in a
  // doorway is nothing, and it keeps the channel free of UI callbacks.
  useEffect(() => {
    if (!offer) return undefined;
    const timer = setInterval(() => {
      setCandidates([...offer.candidates]);
    }, 700);
    return () => clearInterval(timer);
  }, [offer]);

  // An offer outlives this screen if nobody stops it. Fifteen minutes of
  // a stranger being able to propose a key is the documented bound; a
  // screen that was closed should not be spending any of it.
  useEffect(() => () => {
    offerRef.current?.stop();
    offerRef.current = null;
  }, []);

  const read = useCallback((value: string) => {
    setText(value);
    setProblem(null);
    setPending(null);
    const trimmed = value.trim();
    if (!trimmed || !threads) return;
    const kind = threads.classifyThreadArtifact(trimmed);
    if (kind.kind && kind.kind !== "link" && kind.kind !== "seed") {
      // A ticket or an offer pasted here is a real mistake with a real
      // sentence attached, not "invalid input".
      if (kind.kind === "ticket") {
        setProblem("That is a job, not a phone. A job goes to a phone; it does not pair one.");
        return;
      }
      if (kind.kind === "offer") {
        setProblem("That is a job offer. Offers are broadcast; a phone pairs with its own card.");
        return;
      }
    }
    if (kind.kind === "seed") {
      setProblem("That is a seed address — a place to dial, not a phone. It belongs under your own seed.");
      return;
    }
    try {
      const card = threads.parseDeviceCardUrl(trimmed);
      const publicKeyHex = toHex(card.publicKey);
      if (state.devices.some(device => device.publicKeyHex === publicKeyHex)) {
        setProblem("That phone is already paired.");
        return;
      }
      setPending({ name: card.name, publicKeyHex, fingerprint: card.fingerprint });
      setName(card.name || "");
    } catch {
      setProblem("That is not a phone’s pairing code. It should start with wayfind://device#.");
    }
  }, [threads, state.devices]);

  /**
   * A phone, stood in for by this tab.
   *
   * The other side of a pairing is a real device app holding a key this
   * console never sees. There is not one in the room during an
   * evaluation, so this makes a keypair here and pastes its card —
   * exactly the bytes a phone would show. It is labelled a demonstration
   * wherever the device appears afterwards, and the private half stays in
   * this tab: nothing about the protocol is faked, only the hardware.
   */
  const standIn = useCallback(async () => {
    if (!threads) return;
    const keypair = await threads.generateDeviceKeypair();
    const label = name.trim() || `Phone ${state.devices.length + 1}`;
    const card = threads.deviceCardUrl({ publicKey: keypair.publicKey, name: label });
    setText(card);
    setPending({
      name: label,
      publicKeyHex: toHex(keypair.publicKey),
      fingerprint: threads.fingerprint(keypair.publicKey)
    });
    setName(label);
    standInKeys.set(toHex(keypair.publicKey), toHex(keypair.privateKey));
  }, [threads, name, state.devices.length]);

  return (
    <Panel
      eyebrow="Pair a phone"
      aside={
        <button type="button" className="button button--small" onClick={onClose}>Close</button>
      }
    >
      <p className="subtitle">
        This console keeps the phone’s public half and nothing else; the key that opens jobs never
        leaves that phone.
      </p>

      {channel ? (
        <div className="stack stack--tight" style={{ marginTop: "0.8rem" }}>
          {offer ? (
            <>
              <Note title="Point the phone’s camera at this." tone="signal">
                The code is good for fifteen minutes and grants nothing on its own — anyone who
                photographs it can offer a key, and none of them is paired until you compare the
                eight characters below and press the button.
              </Note>
              <Qr value={offerUrl} alt="Pairing code for a phone to scan" />
              {candidates.length === 0 ? (
                <p className="small muted">Waiting for a phone to answer…</p>
              ) : (
                <ul className="rows" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {candidates.map(candidate => (
                    <li key={candidate.publicKeyHex} className="rowcard">
                      <div className="row row--between">
                        <div>
                          <code className="fingerprint fingerprint--loud wf-code">
                            {candidate.fingerprint}
                          </code>
                          <span className="rowcard__sub"> {candidate.name || "A phone"}</span>
                        </div>
                        <button
                          type="button"
                          className="button button--small button--primary"
                          onClick={() => {
                            void (async () => {
                              const chosen = await offer.confirm(candidate.publicKeyHex, {
                                name: candidate.name
                              });
                              if (!chosen) return;
                              onEnrol({
                                id: localId("device"),
                                name: chosen.name || "A phone",
                                publicKeyHex: chosen.publicKeyHex,
                                fingerprint: chosen.fingerprint,
                                enrolledUnixSeconds: nowSeconds(),
                                retiredUnixSeconds: null,
                                standInPrivateKeyHex: null
                              });
                              offer.stop();
                              offerRef.current = null;
                              setOffer(null);
                              setCandidates([]);
                            })();
                          }}
                        >
                          They match — pair it
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="row row--end">
                <button
                  type="button"
                  className="button button--small button--ghost"
                  onClick={() => {
                    offer.stop();
                    offerRef.current = null;
                    setOffer(null);
                    setCandidates([]);
                  }}
                >
                  Stop showing it
                </button>
              </div>
            </>
          ) : (
            <div className="row">
              <button type="button" className="button button--primary" onClick={() => { void startPairing(); }}>
                Show a code for the phone to scan
              </button>
              <span className="small muted">
                The phone answers over the mesh, so nothing has to be typed or pasted anywhere.
              </span>
            </div>
          )}
          {pairingProblem ? (
            <div className="banner banner--halt" role="alert">
              <span className="banner__mark">No</span>
              <p className="banner__text">{pairingProblem}</p>
            </div>
          ) : null}
          <hr className="divider" />
        </div>
      ) : null}

      <p className="small muted" style={{ marginTop: "0.6rem" }}>
        {channel
          ? "Or, if the phone cannot reach the mesh: open Wayfind on it and paste the code it shows."
          : "Open Wayfind on the " + vocabulary.driver + "\u2019s phone and read out the code it shows. Paste it here."}
      </p>

      <div className="field" style={{ marginTop: "0.7rem" }}>
        <label className="field__label" htmlFor="pair-card">The code the phone is showing</label>
        <textarea
          id="pair-card"
          className="textarea"
          placeholder="wayfind://device#…"
          value={text}
          onChange={event => read(event.target.value)}
        />
      </div>

      {problem ? (
        <div className="banner banner--halt" role="alert" style={{ marginTop: "0.6rem" }}>
          <span className="banner__mark">No</span>
          <p className="banner__text">{problem}</p>
        </div>
      ) : null}

      {pending ? (
        <div className="stack stack--tight" style={{ marginTop: "0.8rem" }}>
          <Note title="Read this out loud before you pair it." tone="signal">
            The phone is showing the same eight characters. If they do not match, you are pairing
            somebody else’s phone — four bytes is not a cryptographic binding and is not pretending
            to be one, it is a comparison two people can actually make.
          </Note>
          <div className="row">
            <code className="fingerprint fingerprint--loud wf-code">{pending.fingerprint}</code>
          </div>
          <label className="field">
            <span className="field__label">What to call it</span>
            <input
              className="input"
              value={name}
              placeholder="Marco — Pixel 8"
              onChange={event => setName(event.target.value)}
            />
          </label>
          <div className="row row--end">
            <button
              type="button"
              className="button button--primary"
              onClick={() => {
                onEnrol({
                  id: localId("device"),
                  name: name.trim() || pending.name || "A phone",
                  publicKeyHex: pending.publicKeyHex,
                  fingerprint: pending.fingerprint,
                  enrolledUnixSeconds: nowSeconds(),
                  retiredUnixSeconds: null,
                  standInPrivateKeyHex: standInKeys.get(pending.publicKeyHex) ?? null
                });
                setText("");
                setPending(null);
                setName("");
              }}
            >
              They match — pair it
            </button>
          </div>
        </div>
      ) : null}

      {/*
        Evaluation only, and absent otherwise (`config.evaluation`). A
        stand-in is a device key living in this tab, so a job sealed to
        one opens in a browser and on no phone: offered beside the real
        control on a real evening, it is a way to seal a customer's
        delivery to a driver who does not exist. Nothing downstream can
        catch that — the enrolment is cryptographically perfect.
      */}
      {config.evaluation ? (
        <>
          <hr className="divider" />
          <div className="row">
            <button type="button" className="button button--small button--ghost" onClick={() => { void standIn(); }}>
              Stand in for a phone
            </button>
            <span className="small muted">
              A demonstration, on because this address carries <code className="mono">?evaluate=1</code>.
              Makes a real device keypair in this tab and pastes the card a phone would show, so the
              rest of this can be walked through without one in the room. A job sealed to it can be
              opened here and by no driver.
            </span>
          </div>
        </>
      ) : null}
    </Panel>
  );
}

/** Private halves of stand-in phones, so a demonstration can open what it was sent. */
const standInKeys = new Map<string, string>();

export function Roster({
  state,
  vocabulary,
  onPair,
  onRename,
  onRetire
}: {
  state: FleetState;
  vocabulary: Vocabulary;
  onPair(): void;
  onRename(id: string, name: string): void;
  onRetire(id: string): void;
}): JSX.Element {
  const active = state.devices.filter(device => device.retiredUnixSeconds == null);
  const retired = state.devices.filter(device => device.retiredUnixSeconds != null);

  return (
    <Panel
      eyebrow={`${vocabulary.Driver}s`}
      aside={<button type="button" className="button button--small" onClick={onPair}>Pair a phone</button>}
    >
      <div className="rows">
        {active.map(device => (
          <div key={device.id} className="rowcard">
            <div className="rowcard__main">
              <input
                className="input"
                style={{ border: "0", padding: "0.1rem 0", background: "transparent", fontWeight: 600 }}
                value={device.name}
                aria-label={`Name for ${device.fingerprint}`}
                onChange={event => onRename(device.id, event.target.value)}
              />
              <span className="rowcard__sub">Paired {dateWords(device.enrolledUnixSeconds)}</span>
            </div>
            <code className="fingerprint wf-code">{device.fingerprint}</code>
            {device.standInPrivateKeyHex ? <span className="chip">Stand-in</span> : null}
            <button
              type="button"
              className="button button--small button--danger"
              onClick={() => onRetire(device.id)}
            >
              Retire
            </button>
          </div>
        ))}
        {!active.length ? (
          <p className="small muted">No phones paired yet.</p>
        ) : null}
      </div>

      {retired.length ? (
        <>
          <hr className="divider" />
          <p className="small muted">
            Retired: {retired.map(device => device.name).join(", ")}. Jobs already sealed to a
            retired phone still open on it — a seal is not a subscription, and there is nothing to
            revoke. What stops is being sent anything new.
          </p>
        </>
      ) : null}

      <hr className="divider" />
      <Copyable
        label="This console, as a phone"
        value={consoleCardHint(state)}
        hint={
          "Every job is sealed to this console as well as to the driver, which is what lets you "
          + "cancel one, hand it to somebody else, or open a doorstep photo. A browser cannot hold "
          + "this key the way a phone’s keystore can, and it is worth knowing that."
        }
      />
    </Panel>
  );
}

function consoleCardHint(state: FleetState): string {
  return state.consoleDevice.publicKeyHex
    ? `${state.consoleDevice.name} · ${state.consoleDevice.publicKeyHex.slice(0, 16)}…`
    : "—";
}
